import {
  ApolloClient,
  InMemoryCache,
  fromPromise,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import { createClient } from "graphql-ws";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { logout } from "../utils/authUtils";

const GRAPHQL_ENDPOINT = process.env.REACT_APP_API_URL || "";
const WS_ENDPOINT = GRAPHQL_ENDPOINT.replace(/^http/, "ws");

// Single in-flight refresh promise. Any caller (HTTP errorLink, WS retryWait,
// concurrent queries) awaits the SAME promise so the rotated refresh token is
// only sent to the backend once. Without this, a second concurrent call would
// send the now-stale refresh token, the backend would not find it in the DB,
// and the frontend would log the user out.
let refreshPromise: Promise<boolean> | null = null;

export const refreshTokens = (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async (): Promise<boolean> => {
    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) return false;

    const query = `mutation RefreshToken($refreshToken: String!) {
      refreshToken(refreshToken: $refreshToken) {
        accessToken
        refreshToken
        user {
          id
          name
          username
          email
          tenantId
          roleId
          role
          phoneNumber
          createdAt
          updatedAt
          permissions
        }
      }
    }`;

    try {
      const res = await fetch(GRAPHQL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query, variables: { refreshToken } }),
      });
      const json = await res.json();
      if (!json.errors && json.data?.refreshToken) {
        const payload = json.data.refreshToken;
        localStorage.setItem("token", payload.accessToken);
        localStorage.setItem("refreshToken", payload.refreshToken);
        localStorage.setItem("profile", JSON.stringify(payload.user));
        return true;
      }
      return false;
    } catch {
      return false;
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

const uploadLink = createUploadLink({
  uri: GRAPHQL_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem("token");
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors) return;

  const hasUnauth = graphQLErrors.some(
    (err) => err.extensions?.code === "UNAUTHENTICATED",
  );
  if (!hasUnauth) return;

  return fromPromise(
    refreshTokens()
      .then((success) => {
        if (!success) {
          logout().catch(console.error);
          return false;
        }
        return true;
      })
      .catch(() => {
        logout().catch(console.error);
        return false;
      }),
  )
    .filter((success): success is true => !!success)
    .flatMap(() => {
      const token = localStorage.getItem("token");
      operation.setContext(({ headers = {} }: Record<string, any>) => ({
        headers: { ...headers, authorization: token ? `Bearer ${token}` : "" },
      }));
      return forward(operation);
    });
});

// Subscription events (e.g. screenStatusChanged) arrive ONLY while the
// WebSocket is up — anything published during a disconnect (laptop sleep,
// network change, backend restart) is lost, leaving caches patched by those
// events stale until a manual refresh. Consumers register here to backfill
// (refetch) whenever the socket comes back after a drop.
type WsReconnectListener = () => void;
const wsReconnectListeners = new Set<WsReconnectListener>();
let wsHasConnectedOnce = false;

export const onWsReconnected = (cb: WsReconnectListener): (() => void) => {
  wsReconnectListeners.add(cb);
  return () => {
    wsReconnectListeners.delete(cb);
  };
};

const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_ENDPOINT,
    keepAlive: 12_000,
    on: {
      connected: () => {
        if (wsHasConnectedOnce) {
          wsReconnectListeners.forEach((cb) => {
            try {
              cb();
            } catch (err) {
              console.error("[WS] reconnect listener failed:", err);
            }
          });
        }
        wsHasConnectedOnce = true;
      },
    },
    connectionParams: () => {
      const token = localStorage.getItem("token");
      return {
        authorization: token ? `Bearer ${token}` : "",
      };
    },
    shouldRetry: () => true,
    retryWait: async (retries) => {
      // Refresh the JWT before reconnecting so the next attempt uses a valid token
      try {
        await refreshTokens();
      } catch {
        // If refresh fails, still attempt reconnect with existing token
      }
      // Exponential backoff capped at 30s
      await new Promise((resolve) =>
        setTimeout(resolve, Math.min(1000 * 2 ** retries, 30_000)),
      );
    },
  }),
);

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  errorLink.concat(authLink).concat(uploadLink),
);

const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
});

export default client;
